// client/src/pages/admin/AdminOrders.js
// Week 6 Day 35-37: Order Monitoring System
// Admin order monitoring page with advanced filtering

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Clock, CheckCircle, XCircle, TrendingUp,
  Search, Filter, Download, Eye, AlertTriangle, MessageSquare,
  ChevronDown, ChevronUp, Calendar, DollarSign, Users,
  ArrowUpDown, Trash2, X, Flag, CheckSquare, Square
} from 'lucide-react';
import api from '../../services/api';
import useRangeSelect from '../../hooks/useRangeSelect';
import DragSelectOverlay from '../../components/common/DragSelectOverlay';
import FloatingActionBar from '../../components/common/FloatingActionBar';
import ConfirmModal from '../../components/common/ConfirmModal';

const AdminOrders = () => {
  const navigate = useNavigate();

  // State
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', icon: 'warning', confirmLabel: 'Confirm', confirmColor: 'red', onConfirm: () => {} });
  const openConfirm = (opts) => setConfirmModal({ isOpen: true, ...opts });
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    wholesalerId: '',
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    isSimple: 'all'
  });
  const [wholesalers, setWholesalers] = useState([]);

  // Pagination
  const [pagination, setPagination] = useState({
    limit: 50,
    skip: 0,
    total: 0,
    hasMore: false
  });

  // Mobile filter drawer
  const [showFilters, setShowFilters] = useState(false);

  // Use shared selection hook
  const {
    selectedIds, setSelectedIds, handleItemClick, toggleSelect,
    selectAll, clearSelection, dragProps, isDragging, dragRect, dragSelectedIds
  } = useRangeSelect(orders, '_id');

  // Fetch wholesalers for filter dropdown
  useEffect(() => {
    const fetchWholesalers = async () => {
      try {
        const res = await api.adminAPI.getAllWholesalers();
        setWholesalers(res.data?.wholesalers || res.data || []);
      } catch (err) { console.error('Failed to fetch wholesalers:', err); }
    };
    fetchWholesalers();
  }, []);

  // Fetch orders
  useEffect(() => {
    fetchOrders();
    fetchAnalytics();
  }, [filters, pagination.skip]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        limit: pagination.limit,
        skip: pagination.skip
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });

      const response = await api.adminAPI.getOrders(params);

      const ordersArray = response.data?.orders || response.data || [];

      if (Array.isArray(ordersArray)) {
        setOrders(ordersArray);
      } else {
        setOrders([]);
      }

      setPagination(prev => ({
        ...prev,
        total: response.data?.pagination?.total || 0,
        hasMore: response.data?.pagination?.hasMore || false
      }));
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
      setPagination(prev => ({
        ...prev,
        total: 0,
        hasMore: false
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const data = await api.adminAPI.getOrderAnalytics(params);
      setAnalytics(data.data || data || null);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalytics(null);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  // Toggle sort order
  const toggleSortOrder = () => {
    setFilters(prev => ({
      ...prev,
      sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  };

  // EXPORT CSV FUNCTION
  const handleExport = async () => {
    try {
      setExporting(true);

      const params = { ...filters };
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });

      const response = await api.adminAPI.exportOrders(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `orders_export_${dateStr}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error exporting orders:', error);
      toast.error('Error exporting orders: ' + (error.response?.data?.message || error.message));
    } finally {
      setExporting(false);
    }
  };

  // Bulk action handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === orders.length) {
      clearSelection();
    } else {
      selectAll();
    }
  };

  const handleBulkStatus = async (status) => {
    try {
      await api.adminAPI.bulkUpdateOrderStatus([...selectedIds], status);
      clearSelection();
      fetchOrders();
      fetchAnalytics();
    } catch { toast.error('Bulk status update failed'); }
  };

  const handleBulkDelete = async () => {
    openConfirm({
      title: 'Delete Selected Orders',
      message: `Permanently delete ${selectedIds.size} order${selectedIds.size > 1 ? 's' : ''}? This cannot be undone.`,
      icon: 'danger',
      confirmLabel: 'Delete',
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirm();
        try {
          await api.adminAPI.bulkDeleteOrders([...selectedIds]);
          clearSelection();
          fetchOrders();
          fetchAnalytics();
        } catch { toast.error('Bulk delete failed'); }
      }
    });
  };

  const handleBulkProblem = async (isProblem) => {
    try {
      await api.adminAPI.bulkFlagOrders([...selectedIds], isProblem);
      clearSelection();
      fetchOrders();
      fetchAnalytics();
    } catch { toast.error('Bulk flag failed'); }
  };

  const handleDeleteAll = async () => {
    const w = filters.wholesalerId ? wholesalers.find(w => w._id === filters.wholesalerId) : null;
    const target = w ? `all orders for "${w.companyName}"` : 'ALL orders in the system';
    openConfirm({
      title: 'Delete All Orders',
      message: `This will permanently DELETE ${target}. This action cannot be undone!`,
      icon: 'danger',
      confirmLabel: 'Delete All',
      confirmColor: 'red',
      onConfirm: async () => {
        closeConfirm();
        try {
          const res = await api.adminAPI.deleteAllOrders(filters.wholesalerId || undefined);
          toast.success(`Deleted ${res.data.deleted} orders`);
          clearSelection();
          fetchOrders();
          fetchAnalytics();
        } catch { toast.error('Delete all orders failed'); }
      }
    });
  };

  // View order details
  const viewOrderDetails = (orderId) => {
    navigate(`/admin/order/${orderId}`);
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
      processing: 'bg-purple-100 text-purple-800 border-purple-300',
      shipped: 'bg-indigo-100 text-indigo-800 border-indigo-300',
      delivered: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  // Pagination handlers
  const handlePrevPage = () => {
    setPagination(prev => ({
      ...prev,
      skip: Math.max(0, prev.skip - prev.limit)
    }));
  };

  const handleNextPage = () => {
    setPagination(prev => ({
      ...prev,
      skip: prev.skip + prev.limit
    }));
  };

  return (
    <div className={`min-h-screen bg-gray-50 p-4 md:p-6 ${selectedIds.size > 0 ? 'pb-24' : ''}`}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Order Monitoring
        </h1>
        <p className="text-gray-600">
          Monitor and manage all platform orders
        </p>
      </div>

      {/* Stats Cards */}
      {analytics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Orders */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <ShoppingCart className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">
                {analytics.summary.totalOrders}
              </span>
            </div>
            <p className="text-sm text-gray-600">Total Orders</p>
            <p className="text-xs text-gray-500 mt-1">
              €{analytics.summary.totalRevenue.toFixed(2)} total
            </p>
          </div>

          {/* Active Orders */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 text-green-600" />
              <span className="text-2xl font-bold text-gray-900">
                {analytics.summary.activeOrders}
              </span>
            </div>
            <p className="text-sm text-gray-600">Active Orders</p>
            <p className="text-xs text-gray-500 mt-1">
              In progress
            </p>
          </div>

          {/* Completed Orders */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-purple-600" />
              <span className="text-2xl font-bold text-gray-900">
                {analytics.summary.completedOrders}
              </span>
            </div>
            <p className="text-sm text-gray-600">Completed</p>
            <p className="text-xs text-gray-500 mt-1">
              Delivered successfully
            </p>
          </div>

          {/* Problem Orders */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <span className="text-2xl font-bold text-gray-900">
                {analytics.summary.problemOrders}
              </span>
            </div>
            <p className="text-sm text-gray-600">Problem Orders</p>
            <p className="text-xs text-gray-500 mt-1">
              Requires attention
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <h2 className="font-semibold text-gray-900">Filters</h2>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden text-gray-600"
            >
              {showFilters ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className={`p-4 ${showFilters ? 'block' : 'hidden md:block'}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Order #, business, wholesaler..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Wholesaler */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Wholesaler
              </label>
              <select
                value={filters.wholesalerId}
                onChange={(e) => handleFilterChange('wholesalerId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Wholesalers</option>
                {wholesalers.map(w => (
                  <option key={w._id} value={w._id}>{w.companyName}</option>
                ))}
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                onClick={(e) => e.target.showPicker?.()}
                onFocus={(e) => e.target.showPicker?.()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                onClick={(e) => e.target.showPicker?.()}
                onFocus={(e) => e.target.showPicker?.()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
              />
            </div>

            {/* Min Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Amount (€)
              </label>
              <input
                type="number"
                value={filters.minAmount}
                onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                placeholder="0.00"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Max Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Amount (€)
              </label>
              <input
                type="number"
                value={filters.maxAmount}
                onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                placeholder="0.00"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Simple Orders Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order Mode
              </label>
              <select
                value={filters.isSimple}
                onChange={(e) => handleFilterChange('isSimple', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All</option>
                <option value="true">Simple</option>
                <option value="false">Full</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="createdAt">Date Created</option>
                <option value="totalAmount">Total Amount</option>
                <option value="orderNumber">Order Number</option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <button
                onClick={toggleSortOrder}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
              >
                <ArrowUpDown className="w-4 h-4" />
                {filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilters({
                search: '',
                status: 'all',
                wholesalerId: '',
                startDate: '',
                endDate: '',
                minAmount: '',
                maxAmount: '',
                sortBy: 'createdAt',
                sortOrder: 'desc'
              })}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Clear Filters
            </button>

            <button
              onClick={handleExport}
              disabled={exporting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar (inline, always visible when items selected) */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-blue-800">{selectedIds.size} order{selectedIds.size > 1 ? 's' : ''} selected</span>
          <div className="w-px h-6 bg-blue-200" />
          <select onChange={(e) => { if (e.target.value) handleBulkStatus(e.target.value); e.target.value = ''; }}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium border-0 cursor-pointer"
            defaultValue="">
            <option value="" disabled>Change Status...</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button onClick={() => handleBulkProblem(true)}
            className="px-3 py-1.5 bg-yellow-500 text-white rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-yellow-600">
            <Flag className="w-3.5 h-3.5" /> Flag Problem
          </button>
          <button onClick={() => handleBulkProblem(false)}
            className="px-3 py-1.5 bg-gray-500 text-white rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-gray-600">
            <Flag className="w-3.5 h-3.5" /> Unflag
          </button>
          <button onClick={handleBulkDelete}
            className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-red-700">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
          <button onClick={clearSelection}
            className="ml-auto px-3 py-1.5 text-blue-600 hover:text-blue-800 text-sm font-medium">
            Clear Selection
          </button>
        </div>
      )}

      {/* Delete All Orders */}
      <div className="mb-4">
        <button onClick={handleDeleteAll}
          className="px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-red-800">
          <Trash2 className="w-4 h-4" /> {filters.wholesalerId
            ? `Delete All Orders for ${wholesalers.find(w => w._id === filters.wholesalerId)?.companyName || 'Selected Wholesaler'}`
            : 'Delete All Orders'}
        </button>
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-lg shadow" {...dragProps}>
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="p-8 text-center">
            <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No orders found</p>
            <p className="text-sm text-gray-500 mt-1">
              Try adjusting your filters
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <button onClick={toggleSelectAll}>
                        {selectedIds.size === orders.length && orders.length > 0 ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4 text-gray-400" />}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Wholesaler
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order._id} data-select-id={order._id} className={`hover:bg-gray-50 ${selectedIds.has(order._id) || dragSelectedIds.has(order._id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-4 w-10">
                        <button onClick={(e) => handleItemClick(order._id, e)}>
                          {selectedIds.has(order._id) ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4 text-gray-300" />}
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {order.orderNumber || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {order.itemsCount || 0} items
                            </div>
                          </div>
                          {order.isProblem && (
                            <AlertTriangle className="w-4 h-4 text-red-600" />
                          )}
                          {order.hasAdminNotes && (
                            <MessageSquare className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900">
                          {order.business?.name || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.business?.contact || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900">
                          {order.wholesaler?.name || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.wholesaler?.contact || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(order.status || 'pending')}`}>
                          {order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          €{(order.totalAmount || 0).toFixed(2)}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {order.createdAt ? new Date(order.createdAt).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.createdAt ? new Date(order.createdAt).toLocaleTimeString() : ''}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => viewOrderDetails(order._id)}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          <span className="text-sm">View</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {orders.map((order) => (
                <div key={order._id} data-select-id={order._id} className={`p-4 ${selectedIds.has(order._id) || dragSelectedIds.has(order._id) ? 'bg-blue-50' : ''}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <button onClick={(e) => handleItemClick(order._id, e)} className="flex-shrink-0">
                        {selectedIds.has(order._id) ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4 text-gray-300" />}
                      </button>
                      <div>
                        <div className="font-medium text-gray-900">
                          {order.orderNumber || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.itemsCount || 0} items
                        </div>
                      </div>
                      {order.isProblem && (
                        <AlertTriangle className="w-4 h-4 text-red-600" />
                      )}
                      {order.hasAdminNotes && (
                        <MessageSquare className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(order.status || 'pending')}`}>
                      {order.status ? order.status.charAt(0).toUpperCase() + order.status.slice(1) : 'Pending'}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm mb-3">
                    <div>
                      <span className="text-gray-600">Business:</span>{' '}
                      <span className="font-medium">{order.business?.name || 'Unknown'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Wholesaler:</span>{' '}
                      <span className="font-medium">{order.wholesaler?.name || 'Unknown'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Amount:</span>{' '}
                      <span className="font-medium">€{(order.totalAmount || 0).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Date:</span>{' '}
                      <span className="font-medium">
                        {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => viewOrderDetails(order._id)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total} orders
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handlePrevPage}
                  disabled={pagination.skip === 0}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={!pagination.hasMore}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Floating Bulk Action Bar */}
      <FloatingActionBar
        count={selectedIds.size}
        onClear={clearSelection}
        onSelectAll={selectedIds.size < orders.length ? selectAll : undefined}
      >
        <select onChange={(e) => { if (e.target.value) handleBulkStatus(e.target.value); e.target.value = ''; }}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium text-white border-0 cursor-pointer"
          defaultValue="">
          <option value="" disabled>Status...</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button onClick={() => handleBulkProblem(true)}
          className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium flex items-center gap-1">
          <Flag className="w-3.5 h-3.5" /> Flag
        </button>
        <button onClick={() => handleBulkProblem(false)}
          className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-sm font-medium flex items-center gap-1">
          <Flag className="w-3.5 h-3.5" /> Unflag
        </button>
        <button onClick={handleBulkDelete}
          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-1">
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </FloatingActionBar>

      <DragSelectOverlay isDragging={isDragging} dragRect={dragRect} />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        icon={confirmModal.icon}
        confirmLabel={confirmModal.confirmLabel}
        confirmColor={confirmModal.confirmColor}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default AdminOrders;